import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, MessageCircleIcon } from 'lucide-react';
import { PressStrip } from '../shared/PressStrip';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { Rail } from '../shared/Rail';
import { PageHero } from '../shared/PageHero';
import { CreatorRail } from './CreatorRail';
import { closedDaysLabel, founder, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { policy, policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { staffList } from '../../core/rules/salonDays';
import { visitStatus } from '../../core/rules/visitStatus';
import { ease } from '../../core/format/motion';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { useNow } from '../../hooks/useNow';
import { fill } from '../../utils/copy';
import { homeBestsellers } from '../home/homeRules';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';

/** Tijdelijk sfeerbeeld (docs/BEELDEN.md, "tijdelijk, vervangen vóór livegang"): niet de salon zelf. */
const SALON_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/0ed04ad3-0600-47a3-bc34-389527c1fbf3.jpg';
/** Zoveel producten van "de plank van Sarah" (schuifbaar). */
const SHELF_COUNT = 6;

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. */
const facts = (withColleague: boolean) => [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
},
// Met een collega klopt "altijd door mij" niet meer.
{
  value: 'Eén',
  label: withColleague ? 'cliënt tegelijk, met alle aandacht' : 'cliënt tegelijk, altijd door mij behandeld'
}, {
  value: `${policy.responseHours} uur`,
  label: 'om persoonlijk te reageren op je bericht'
}];

/**
 * Over Sarah, als één verhaal (docs/VORMGEVING.md §3): wie (hero, sand) · haar verhaal en feiten
 * (sand) · waar ze voor staat (klei) · wie haar aanraden (pers, shell; vaste cliënten, sand) ·
 * wat ze zelf gebruikt (shell, schuifbaar) · kom kennismaken (blush).
 *
 * Collega's staan niet hier maar op de salonpagina, waar je kiest bij wie je komt. Adres en alle
 * openingstijden staan op Contact; hier één regel wanneer je terecht kunt.
 */
export function About() {
  const pages = usePages();
  const site = useSite();
  const reduce = useReducedMotion();
  const now = useNow();
  const {
    openBooking
  } = useOverlayActions();
  const {
    products
  } = useCatalog();
  const copy = pages.about;
  // Feiten moeten kloppen, ook als de collega (nog) niet op de site staat.
  const withColleague = staffList(site.staff).some(s => s.role === 'staff' && s.active);
  const shelf = homeBestsellers(products, SHELF_COUNT);
  const status = visitStatus(site.openingHours, site.salonExceptions, now);
  const closed = closedDaysLabel(site.openingHours);
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de hero; de rest leest eronder rustig verder.
  const [lead, ...rest] = copy.hero.paragraphs;
  return <>
      {/* Hero: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5534" data-mp="i3hbc496ua8z" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5535" data-mp="e3gxoltwy32z">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-5536" data-mp="q2cirmse2gbv">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <Link to="/routines" className={btnSecondaryLg} data-id="element-5537" data-mp="2ecmy8g7zzqf">
                  {fill(copy.hero.secondaryCta)}
                </Link>}
            </>} data-id="element-5533" data-mp="2jx6xf493aoh1" />

      {/* Het verhaal, en de feiten die ze kan onderbouwen. De feiten komen één voor één binnen. */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-label={`Over ${founder.firstName}`} data-id="element-5538" data-mp="3tkmbaa0800ns">
        <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5539" data-mp="3fkutdy6l204i">
          {rest.length > 0 && <div className="max-w-2xl space-y-5 text-[17px] leading-relaxed text-muted lg:col-span-7" data-id="element-5540" data-mp="30ynyphd279hn">
              {rest.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} className={i === 0 ? 'text-ink' : undefined} data-id="element-5541" data-mp="2w0mhl1z71k8t" data-mpi={i}>
                  {fill(p)}
                </p>)}
            </div>}
          {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
          <ul className={`grid gap-6 sm:grid-cols-3 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15 ${rest.length > 0 ? 'lg:col-span-5' : 'lg:col-span-12'}`} data-id="element-5542" data-mp="3jxa0kygeh4mm">
            {facts(withColleague).map((f, i) => <motion.li key={f.label} initial={reduce ? false : {
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '0px 0px -10% 0px'
          }} transition={{
            duration: 0.25,
            delay: i * 0.06,
            ease
          }} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5543" data-mp="3tnuz6lw2q95j" data-mpi={i}>
                <span className="block font-display text-[2rem] font-light text-ink lg:w-28 lg:shrink-0" data-id="element-5544" data-mp="2vclbwl7jgd9x">{f.value}</span>
                <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5545" data-mp="30b4wah6hhh06">{f.label}</span>
              </motion.li>)}
          </ul>
        </div>
      </section>

      {/* Waar ik voor sta: het ene klei-vlak. De foto beweegt licht binnen zijn kader. */}
      <section className="bg-clay text-shell" aria-label="Filosofie" data-id="element-5546" data-mp="3e0ypfbp0unfp">
        <div className="grid lg:grid-cols-12" data-id="element-5547" data-mp="3j0uuzfddamxd">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5548" data-mp="3izjr7fjx9f69">
            <p className="text-[14px] text-shell" data-id="element-5549" data-mp="3s0rlznn8bxvl">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl font-display text-[2.6rem] font-light leading-[1.04] tracking-[-0.015em] sm:text-[3.4rem] lg:text-[4rem]" data-id="element-5550" data-mp="1tmoickqne1c0">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5551" data-mp="1q50ut2cnlr9a">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 font-display text-2xl italic text-blush" data-id="element-5552" data-mp="1lbzagetogzib">{founder.firstName}</figcaption>
          </figure>
          <ParallaxMedia media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/5] w-full sm:aspect-[16/11] lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5553" data-mp="1ccuftjl8lqbf" />
        </div>
      </section>

      <PressStrip data-id="element-5554" data-mp="241xn788f7gsa" />

      <CreatorRail data-id="element-5555" data-mp="2026lwal9pi9i" />

      {/* Haar eigen plank: dezelfde rail als op de homepage. */}
      {shelf.length > 0 && <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="about-shelf" data-id="element-5556" data-mp="1v453lrvo1mz3">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5557" data-mp="ont33k5dft0c">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-5558" data-mp="okx3zshseyab">
              <div className="max-w-2xl" data-id="element-5559" data-mp="jmvlb8o9x0zd">
                <h2 id="about-shelf" className={`${sectionHeading} text-ink`} data-id="element-5560" data-mp="2jqobkmwv7ek3">
                  <HeadingText heading={copy.shelf.heading} data-id="element-5561" data-mp="2jssp7wkkfrxk" />
                </h2>
                {copy.shelf.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-5562" data-mp="2npovk8r2wn8o">{fill(copy.shelf.body)}</p>}
              </div>
              {copy.shelf.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-5563" data-mp="2es4sqvdkiivy">
                  {fill(copy.shelf.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5564" data-mp="32hpko3rp8qhd" />
                </Link>}
            </div>

            <Rail id="about-shelf-rail" label={`De plank van ${founder.firstName}`} className="mt-10 lg:mt-12" stagger items={shelf} itemKey={p => p.id} itemClassName="w-[68%] sm:w-[40%] lg:w-[calc((100%-4.5rem)/3.5)]" renderItem={p => <ProductCard product={p} data-id="element-5566" data-mp="3q834x841ll1e" />} data-id="element-5565" data-mp="2srmj3f6ii47f" />
          </div>
        </section>}

      {/* Kom kennismaken: het slot. Eén knop; de salonpagina en WhatsApp als rustige links. */}
      <section className="bg-blush" aria-labelledby="about-salon" data-id="element-5567" data-mp="19rnbaoztqhzb">
        <div className="mx-auto grid max-w-content items-center gap-12 px-6 py-20 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-32" data-id="element-5568" data-mp="1nkcvcnvslcts">
          <ParallaxMedia media={{
          image: SALON_IMAGE,
          alt: 'Sfeerbeeld: een rustige ruimte met daglicht en linnen'
        }} sizes="(min-width: 1024px) 45vw, 100vw" className="aspect-[4/3] w-full lg:col-span-6 lg:aspect-[5/6]" data-id="element-5569" data-mp="1jirnjgy47oqg" />

          <div className="lg:col-span-6" data-id="element-5570" data-mp="14tlqnzmr2alq">
            <p className="flex items-center gap-2.5 text-[14px] text-ink" data-id="element-5571" data-mp="14un0y1vxyt2e">
              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${status.open ? 'bg-sage' : 'bg-ink/30'}`} data-id="element-5572" data-mp="zthaybto1jwk" />
              {status.label}
            </p>
            {copy.salon.eyebrow && <p className="mt-6 text-[14px] text-ink" data-id="element-5573" data-mp="2b8e0dmtrib4g">{fill(copy.salon.eyebrow)}</p>}
            <h2 id="about-salon" className={`${copy.salon.eyebrow ? 'mt-3' : 'mt-6'} ${sectionHeading} text-ink`} data-id="element-5574" data-mp="2p6u2uk8xxhpc">
              <HeadingText heading={copy.salon.heading} data-id="element-5575" data-mp="2k892mktouy0w" />
            </h2>
            {copy.salon.body && <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-5576" data-mp="2ffpptq2ts6x5">{fill(copy.salon.body)}</p>}

            <address className="mt-8 flex max-w-lg gap-3 border-t border-ink/15 pt-6 text-[15px] not-italic leading-relaxed text-ink" data-id="element-5577" data-mp="2bdut6yvuahpp">
              <MapPinIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-5578" data-mp="26dq9kzzmx6v9" />
              <span data-id="element-5579" data-mp="21it3a0tqpbm2">
                {salon.street}, {salon.postalCode} {salon.locality}
                <span className="block text-muted" data-id="element-5580" data-mp="1whwxtitqphej">
                  Alleen op afspraak{closed ? ` · ${closed.toLowerCase()}` : ''} ·{' '}
                  <Link to="/contact" className="underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5581" data-mp="3qved6ijzr7yh">
                    alle tijden en contact
                  </Link>
                </span>
              </span>
            </address>

            {copy.salon.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`mt-8 ${btnPrimaryLg} w-full sm:w-auto`} data-id="element-5582" data-mp="1dkv33kfr2dr6">
                {fill(copy.salon.cta)}
              </button>}
            {(copy.salon.salonCta || copy.salon.secondaryCta) && <div className="mt-4 flex flex-wrap items-center gap-x-6" data-id="element-5583" data-mp="pnfpoggon1oo">
                {copy.salon.salonCta && <Link to="/behandelingen" className={`${quietLink} text-ink`} data-id="element-5584" data-mp="um0ocbgaddb0">
                    {fill(copy.salon.salonCta)}
                    <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5585" data-mp="yncdygz5tyj7" />
                  </Link>}
                {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-5586" data-mp="2a298u7i0zhyh">
                    <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5587" data-mp="2f12pu9witr3e" />
                    {fill(copy.salon.secondaryCta)}
                    <span className="sr-only" data-id="element-5588" data-mp="ayj4eu30y5y7">{shopLabels.viaWhatsApp}</span>
                  </a>}
              </div>}
          </div>
        </div>
      </section>
    </>;
}